import * as React from "react";
import { Box, BoxProps } from "@mui/material";
import { validations } from "./ValidationRules";
import { ValidatorFormProps } from "../interface/Validator";
import { Validator } from "../types/ValidatorType";

export type FormContextValue = {
  form: {
    attachToForm: (component: any) => void;
    detachFromForm: (component: any) => void;
    instantValidate: boolean;
    debounceTime: number;
  } | null;
};

const FormContext = React.createContext<FormContextValue>({
  form: null,
});

export { FormContext };

class ValidatorForm extends React.Component<ValidatorFormProps & BoxProps> {
  static getValidator = (
    validator: Validator,
    value: any,
    includeRequired: boolean
  ): boolean => {
    let result = true;

    if (validator.validator !== "required" || includeRequired) {
      switch (validator.validator) {
        case "required":
          result = validations.required(value);
          break;

        case "isEmail":
          result = validations.isEmail(value);
          break;

        case "isEmpty":
          result = validations.isEmpty(value);
          break;

        case "allowedExtensions":
          result = validations.allowedExtensions(value, validator.fileTypes);
          break;

        case "isFile":
          result = validations.isFile(value);
          break;

        case "isFloat":
          result = validations.isFloat(value);
          break;

        case "isNumber":
          result = validations.isNumber(value);
          break;

        case "isPositive":
          result = validations.isPositive(value);
          break;

        case "isString":
          result = validations.isString(value);
          break;

        case "matchRegexp":
          result = validations.matchRegexp(value, validator.regexp);
          break;

        case "maxFileSize":
          result = validations.maxFileSize(value, validator.max);
          break;

        case "maxFloat":
          result = validations.maxFloat(value, validator.max);
          break;

        case "maxNumber":
          result = validations.maxNumber(value, validator.max);
          break;

        case "maxStringLength":
          result = validations.maxStringLength(value, validator.max);
          break;

        case "minFloat":
          result = validations.minFloat(value, validator.min);
          break;

        case "minNumber":
          result = validations.minNumber(value, validator.min);
          break;

        case "minStringLength":
          result = validations.minStringLength(value, validator.min);
          break;

        case "trim":
          result = validations.trim(value);
          break;

        default:
          result = true;
          break;
      }
    }

    return result;
  };

  childs: any[] = [];

  errors: any[] = [];

  get instantValidate() {
    return this.props.instantValidate !== undefined
      ? this.props.instantValidate
      : true;
  }

  get debounceTime() {
    return this.props.debounceTime !== undefined ? this.props.debounceTime : 0;
  }

  getFormHelpers = (): FormContextValue => ({
    form: {
      attachToForm: this.attachToForm,
      detachFromForm: this.detachFromForm,
      instantValidate: this.instantValidate,
      debounceTime: this.debounceTime,
    },
  });

  attachToForm = (component: any) => {
    if (this.childs.indexOf(component) === -1) {
      this.childs.push(component);
    }
  };

  detachFromForm = (component: any) => {
    const componentPos = this.childs.indexOf(component);

    if (componentPos !== -1) {
      this.childs = [
        ...this.childs.slice(0, componentPos),
        ...this.childs.slice(componentPos + 1),
      ];
    }
  };

  submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    this.errors = [];

    this.walk(this.childs).then((result: boolean) => {
      if (this.errors.length && this.props.onError) {
        this.props.onError(this.errors);
      }

      if (result) {
        this.props.onSubmit();
      }

      return result;
    });
  };

  walk = (children: any[], dryRun?: boolean): Promise<boolean> => {
    return new Promise((resolve) => {
      if (!Array.isArray(children)) {
        this.walk([children], dryRun).then(resolve);
        return;
      }

      Promise.all(children.map((input) => this.checkInput(input, dryRun))).then(
        (data) => {
          resolve(data.every(Boolean));
        }
      );
    });
  };

  checkInput = (input: any, dryRun?: boolean): Promise<boolean> => {
    return new Promise((resolve) => {
      const validators: Validator[] | undefined = input?.props?.validators;

      if (!validators || !Array.isArray(validators) || validators.length === 0) {
        resolve(true);
        return;
      }

      this.validate(input, true, dryRun).then(resolve);
    });
  };

  validate = (
    input: any,
    includeRequired: boolean,
    dryRun?: boolean
  ): Promise<boolean> => {
    return new Promise((resolve) => {
      const { value } = input.props;

      input.validate(value, includeRequired, dryRun).then((valid: boolean) => {
        if (!valid) {
          this.errors.push(input);
        }

        resolve(valid);
      });
    });
  };

  resetValidations = () => {
    this.childs.forEach((child) => {
      child?.validateDebounced?.cancel?.();
      child?.setState?.({ isValid: true });
    });
  };

  isFormValid = (dryRun = true): Promise<boolean> => {
    return this.walk(this.childs, dryRun);
  };

  render() {
    const {
      onSubmit,
      instantValidate,
      onError,
      debounceTime,
      children,
      ...rest
    } = this.props;

    return (
      <FormContext.Provider value={this.getFormHelpers()}>
        <Box component="form" {...rest} onSubmit={this.submit}>
          {children}
        </Box>
      </FormContext.Provider>
    );
  }
}

export { ValidatorForm as MuiForm };
export default ValidatorForm;